/* Copyright (c) 2021-2026 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { When } from "react-if";
import styled from "styled-components";
import { Text, Navigation, theme, Box, ExtendedPopover } from "@nordcloud/gnui";
import { PopoverTrigger } from "~/components/Popover/PopoverTrigger";
import { PathName } from "./types";

type Item = {
  label: string;
  subLabel?: string;
  onClick: () => void;
  customIcon?: PathName;
  disabled?: boolean;
};

type Props = {
  items: Item[];
  size?: "small" | "large";
  disabled: boolean;
  trigger?: React.ReactNode;
  placement?: "top" | "bottom" | "left" | "right";
  position?: "start" | "center" | "end";
};

type StyleItemProps = {
  isPaddingLeft?: boolean;
  isDisabled?: boolean;
  hasSubLabel?: boolean;
};

const StyleItem = styled(Navigation.Item)<StyleItemProps>`
  flex-direction: ${({ hasSubLabel }) => (hasSubLabel ? "column" : "row")};
  align-items: ${({ hasSubLabel }) => (hasSubLabel ? "flex-start" : "center")};
  height: ${({ hasSubLabel }) => (hasSubLabel ? "fit-content" : "auto")};
  cursor: ${({ isDisabled }) => (isDisabled ? "not-allowed" : "pointer")};
  user-select: none;
  padding-left: ${({ isPaddingLeft }) =>
    isPaddingLeft && theme.spacing.spacing04};
  color: ${({ isDisabled }) => isDisabled && theme.color.text.text03};
  &:hover {
    background-color: ${({ isDisabled }) =>
      isDisabled && "transparent"} !important;
    color: ${({ isDisabled }) =>
      isDisabled && theme.color.text.text03} !important;
  }
`;

export function PopoverControls({
  items,
  size = "small",
  disabled,
  trigger,
  placement,
  position,
}: Props) {
  return (
    <ExtendedPopover
      placement={placement ?? "left"}
      position={position ?? "start"}
      trigger={trigger ?? <PopoverTrigger {...{ size, disabled }} />}
      content={
        <When condition={!disabled}>
          <Box
            innerSpacing="spacing03"
            shadow="shadow04"
            data-testid="options-content"
          >
            <Navigation.Container popoverMenu>
              {items.map((item) => {
                return (
                  <StyleItem
                    key={item.label}
                    isDisabled={item.disabled}
                    hasSubLabel={!!item.subLabel}
                    onClick={item.disabled ? undefined : item.onClick}
                  >
                    <Text ml="2" tag="span">
                      {item.label}
                    </Text>
                    <When condition={!!item.subLabel}>
                      <Text
                        color={theme.color.text.text02}
                        ml="2"
                        tag="span"
                        size="sm"
                      >
                        {item.subLabel}
                      </Text>
                    </When>
                  </StyleItem>
                );
              })}
            </Navigation.Container>
          </Box>
        </When>
      }
    />
  );
}
